// ===================================================================
// KAIXO - Pull to refresh
// ===================================================================
// Gesto "deslizar hacia abajo desde el tope" → recarga la app.
//
// Cómo evitamos pisar el scroll normal:
//   1. Solo armamos el gesto si TODOS los contenedores scrolleables
//      del path tienen scrollTop=0 (estás en el tope).
//   2. Esperamos a un umbral de DEDO ABAJO claro (>= 14 px) antes de
//      "armar" el pull. Antes de eso, el navegador hace su scroll normal.
//   3. Si en cualquier momento el dedo va hacia ARRIBA o se mueve
//      lateralmente más que verticalmente, abortamos el gesto y dejamos
//      el control al scroll nativo para el resto del touch.
//   4. Solo llamamos preventDefault DESPUÉS del umbral — antes el
//      navegador puede iniciar su propio scroll y luego nosotros tomamos
//      control si toca.
// ===================================================================

import React, { useEffect, useRef, useState } from 'react';
import './PullToRefresh.css';

const ARM_THRESHOLD   = 14;        // px abajo antes de "armar" el pull
const TRIGGER_DISTANCE = 80;       // px arrastrados → suelta = refresh
const MAX_PULL         = 140;      // máx visible
const RESISTANCE       = 0.55;

export default function PullToRefresh({ onRefresh, children, disabled = false }) {
    const [pullDist, setPullDist] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    const startYRef = useRef(null);
    const startXRef = useRef(null);
    const armedRef  = useRef(false);    // gesto realmente activo (pasó umbral)
    const candidateRef = useRef(false); // candidato (estamos al tope, esperando)
    const abortedRef = useRef(false);

    useEffect(() => {
        if (disabled) return;

        const onTouchStart = (e) => {
            if (refreshing) return;
            armedRef.current = false;
            candidateRef.current = false;
            abortedRef.current = false;

            // Verificar que NINGÚN scroller en el path tenga scrollTop > 0.
            // Si la lista interna ya está scrolleada, no es candidata.
            if (anyScrollerNotAtTop(e.target)) return;
            if (window.scrollY > 0) return;

            startYRef.current = e.touches[0].clientY;
            startXRef.current = e.touches[0].clientX;
            candidateRef.current = true;
        };

        const onTouchMove = (e) => {
            if (refreshing) return;
            if (!candidateRef.current || abortedRef.current) return;

            const dy = e.touches[0].clientY - startYRef.current;
            const dx = Math.abs(e.touches[0].clientX - startXRef.current);

            // Cancelar si el usuario va más lateral que vertical (swipe nav)
            if (dx > Math.abs(dy) + 6) {
                abortedRef.current = true;
                candidateRef.current = false;
                armedRef.current = false;
                setPullDist(0);
                return;
            }

            // Cancelar si el usuario va hacia arriba (= scroll de la lista)
            if (dy < 0) {
                abortedRef.current = true;
                candidateRef.current = false;
                armedRef.current = false;
                setPullDist(0);
                return;
            }

            // Antes del umbral: dejamos el control al scroll nativo
            if (dy < ARM_THRESHOLD) {
                return;
            }

            // Pasado el umbral: armamos el gesto y bloqueamos el scroll
            armedRef.current = true;
            // Restamos el umbral para que el indicador empiece a 0
            const effective = dy - ARM_THRESHOLD;
            const stretched = Math.min(MAX_PULL, effective * RESISTANCE);
            setPullDist(stretched);
            if (e.cancelable) e.preventDefault();
        };

        const onTouchEnd = async () => {
            if (!armedRef.current) {
                candidateRef.current = false;
                return;
            }
            armedRef.current = false;
            candidateRef.current = false;

            const reached = pullDist >= TRIGGER_DISTANCE;
            if (reached && !refreshing) {
                setRefreshing(true);
                setPullDist(60);
                try {
                    if (onRefresh) await onRefresh();
                    else window.location.reload();
                } catch {}
                setTimeout(() => {
                    setRefreshing(false);
                    setPullDist(0);
                }, 600);
            } else {
                setPullDist(0);
            }
        };

        document.addEventListener('touchstart', onTouchStart, { passive: true });
        document.addEventListener('touchmove', onTouchMove, { passive: false });
        document.addEventListener('touchend', onTouchEnd);
        document.addEventListener('touchcancel', onTouchEnd);
        return () => {
            document.removeEventListener('touchstart', onTouchStart);
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', onTouchEnd);
            document.removeEventListener('touchcancel', onTouchEnd);
        };
    }, [pullDist, refreshing, onRefresh, disabled]);

    const reached = pullDist >= TRIGGER_DISTANCE;

    return (
        <>
            <div
                className={`ptr-indicator ${refreshing ? 'ptr-refreshing' : ''} ${reached ? 'ptr-reached' : ''}`}
                style={{
                    transform: `translateY(${Math.max(0, pullDist - 30)}px)`,
                    opacity: pullDist > 6 || refreshing ? 1 : 0
                }}
            >
                <div className="ptr-spinner" style={{
                    transform: `rotate(${Math.min(360, (pullDist / TRIGGER_DISTANCE) * 270)}deg)`
                }}>↻</div>
            </div>
            {/* El wrapper aplica transform solo cuando el pull está activo,
                pero SIEMPRE mantiene height:100% para no romper la cadena de
                alturas (sin esto, html→body→#root tienen 100% pero este div
                cae a auto y .App pierde la referencia, rompiendo
                adjustResize del teclado). */}
            <div style={{
                height: '100%',
                ...(pullDist > 0 ? {
                    transform: `translateY(${pullDist}px)`,
                    transition: armedRef.current ? 'none' : 'transform 0.25s ease',
                    willChange: 'transform'
                } : {})
            }}>
                {children}
            </div>
        </>
    );
}

// ¿Hay algún ancestro scrolleable con scrollTop > 0?
// Si la lista interna ya está scrolleada hacia abajo, no debemos activar
// el pull-to-refresh — el usuario solo quiere seguir scrolleando.
function anyScrollerNotAtTop(el) {
    let node = el;
    while (node && node !== document.body && node !== document.documentElement) {
        const style = window.getComputedStyle(node);
        const overflowY = style.overflowY;
        const isScroll = overflowY === 'auto' || overflowY === 'scroll';
        if (isScroll && node.scrollHeight > node.clientHeight && node.scrollTop > 0) {
            return true;
        }
        node = node.parentElement;
    }
    return false;
}
